/**
 * Sub-agent interactive chat view rendered from the companion chat panel when
 * a sub-agent (Banan, Vid) is selected. Manages its own managed-agent session
 * via `useManagedAgentChat`, caches messages to the Zustand store when the
 * user switches away, and restores them when switching back.
 *
 * Supports navigating between multiple sessions for the same agent via a
 * pill bar above the message list. Clicking any pill switches the active
 * session in the store — the hook re-initializes against that sessionId,
 * so every session is fully interactive (no read-only mode).
 *
 * Renders in compact mode (same as Marcus side-panel chat) with thinking
 * blocks, tool call cards, markdown formatting, and media display.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAdsWorkspaceStore, type SubAgentId } from "@/features/ads";
import { useSubAgentGeneratedAssets } from "../../hooks/useSubAgentGeneratedAssets";
import { useSubAgentManagedChatSession } from "../../hooks/useSubAgentManagedChatSession";
import { useChatAttachments } from "../../hooks/useChatAttachments";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { dispatchCompanionToolCall } from "../agent-chat/dispatchCompanionToolCall";
import { ChatMessageList } from "../agent-chat/ChatMessageList";
import { CompanionInput } from "../agent-chat/CompanionInput";
import { SessionPillBar } from "../agent-chat/SessionPillBar";
import { shouldSuppressToolCall } from "../agent-chat/toolCallFriendlyNames";
import { useTurnTimer, ThinkingBubble, TurnCompletedPill } from "../TurnIndicator";
import {
  sanitizeDispatchBriefMessages,
  sanitizeImageAdAssistantResults,
} from "./sanitizeDispatchBrief";
import type { UIMessage } from "@ai-sdk/react";
import {
  getChatRenderableSubAgent,
  type ChatRenderableSubAgent,
} from "../../lib/chatAgentRegistry";
import { sanitizeAgentVProviderBranding } from "../../lib/providerBranding";

function sanitizeAgentVDisplayMessages(messages: UIMessage[]): UIMessage[] {
  let changed = false;
  const nextMessages = messages.map((message) => {
    if (!message.parts?.length) return message;
    let partsChanged = false;
    const nextParts = message.parts.map((part) => {
      if (!part || typeof part !== "object") return part;
      const record = part as Record<string, unknown>;
      const next: Record<string, unknown> = { ...record };
      let partChanged = false;
      for (const key of ["text", "thinking", "reasoning", "errorText"]) {
        const value = record[key];
        if (typeof value !== "string") continue;
        const sanitized = sanitizeAgentVProviderBranding(value);
        if (sanitized === value) continue;
        next[key] = sanitized;
        partChanged = true;
        partsChanged = true;
      }
      return partChanged ? (next as typeof part) : part;
    });
    if (!partsChanged) return message;
    changed = true;
    return { ...message, parts: nextParts as UIMessage["parts"] };
  });
  return changed ? nextMessages : messages;
}

// ── Component ───────────────────────────────────────────────────────────────

export function SubAgentChatView({
  agentId,
  hideHeader,
  renderToolPart,
}: {
  agentId: SubAgentId;
  hideHeader?: boolean;
  /** Optional override for tool-call rendering. When provided, both static
   *  `tool-<name>` parts and `dynamic-tool` parts route through this
   *  renderer, replacing the default generic ToolCallCard. Used by
   *  AdsCliCompanion to plug in the Mads compact card. */
  renderToolPart?: (part: Record<string, unknown>, index: number) => React.ReactNode;
}) {
  // Scout is handled by research tab redirect — guard against render
  if (agentId === "scout") return null;
  // Moodboard chat is surfaced from inside the research tab; not rendered here.
  if (agentId === "moodboard") return null;

  const meta = getChatRenderableSubAgent(agentId);
  if (!meta) return null;

  return (
    <SubAgentChatViewInner
      agentId={agentId}
      meta={meta}
      hideHeader={hideHeader}
      renderToolPart={renderToolPart}
    />
  );
}

function SubAgentChatViewInner({
  agentId,
  meta,
  hideHeader,
  renderToolPart,
}: {
  agentId: SubAgentId;
  meta: ChatRenderableSubAgent;
  hideHeader?: boolean;
  renderToolPart?: (part: Record<string, unknown>, index: number) => React.ReactNode;
}) {
  const Icon = meta.chatIcon;
  const voiceInputEnabled = meta.supportsVoiceInput;

  const chatState = useAdsWorkspaceStore((s) => s.subAgentChats[agentId]);
  const setActiveSubAgent = useAdsWorkspaceStore((s) => s.setActiveSubAgent);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState("");
  const {
    attachedImages,
    isUploadingImage,
    attachmentError,
    handleImageAttach,
    removeAttachment: handleRemoveImage,
    clearAttachments,
    clearAttachmentError,
  } = useChatAttachments({
    allowVideo: true,
    drainPendingAttachment: "active-chat",
  });
  const { handleImageGenerated, handleVideoGenerated, setLiveSessionId } =
    useSubAgentGeneratedAssets(agentId, chatState.sessionId);

  const handleToolUse = useCallback(
    (event: { name: string; input: unknown }) => {
      dispatchCompanionToolCall(event.name, event.input, {
        source: `sub-agent:${agentId}`,
      });
    },
    [agentId],
  );

  const {
    messages,
    error,
    sendMessage,
    stopMessage,
    queuedMessageCount,
    isLoading,
    isRestoringHistory,
    liveSessionId,
    activePillSessionId,
    sessionPills,
    handleSelectSession,
  } = useSubAgentManagedChatSession({
    agentId,
    backendAgentId: meta.backendAgentId,
    runtimeTarget: meta.runtimeTarget,
    agentName: meta.agentName,
    chatState,
    onImageGenerated: agentId === "banan" ? handleImageGenerated : undefined,
    onVideoGenerated:
      agentId === "vid" ||
      agentId === "higgsfield" ||
      agentId === "higgsfield_cli" ||
      agentId === "agent_v"
        ? handleVideoGenerated
        : undefined,
    onToolUse: handleToolUse,
  });

  // Keep the asset-tagging ref in sync so newly-generated images/videos get
  // stamped with the live session id (used by ChatPanelHeader's asset strip).
  useEffect(() => {
    setLiveSessionId(liveSessionId);
  }, [liveSessionId, setLiveSessionId]);

  // Auto-scroll on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Turn timer — same UX as Marcus in ChatPanel. Users get a live "Agent Vid
  // is thinking Xs" while work is in flight, then a "Took Xs" pill once the
  // turn lands, so long Kling polls feel like progress instead of a hang.
  const { elapsedMs: turnElapsedMs, lastDurationMs: lastTurnDurationMs } = useTurnTimer(isLoading);

  // Decide whether to show the thinking bubble. Hide it when the trailing
  // rendered part is a tool still executing — the ToolCallCard itself shows
  // progress and we don't want to double-signal. Skip over suppressed parts
  // (JWT regen, etc.) so a trailing noise-bash doesn't hide the bubble.
  const lastSubMessage = messages[messages.length - 1];
  let lastVisiblePart:
    | { type?: string; state?: string; toolName?: string; input?: unknown }
    | undefined;
  if (lastSubMessage?.parts?.length) {
    for (let i = lastSubMessage.parts.length - 1; i >= 0; i -= 1) {
      const p = lastSubMessage.parts[i] as {
        type?: string;
        state?: string;
        toolName?: string;
        input?: unknown;
      };
      const isToolLike =
        typeof p.type === "string" && (p.type === "dynamic-tool" || p.type.startsWith("tool-"));
      if (isToolLike) {
        const tn = p.type === "dynamic-tool" ? p.toolName : p.type!.slice(5);
        if (tn && shouldSuppressToolCall(tn, p.input)) continue;
      }
      lastVisiblePart = p;
      break;
    }
  }
  const isLastPartToolRunning =
    !!lastVisiblePart &&
    typeof lastVisiblePart.type === "string" &&
    (lastVisiblePart.type === "dynamic-tool" || lastVisiblePart.type.startsWith("tool-")) &&
    (lastVisiblePart.state === "input-streaming" || lastVisiblePart.state === "input-available");
  const showThinkingBubble = isLoading && !isLastPartToolRunning;

  const sendInputMessage = useCallback(
    (rawText: string) => {
      const textValue = rawText.trim();
      const hasContent = textValue.length > 0 || attachedImages.length > 0;
      if (!hasContent) return;
      const images = attachedImages.length > 0 ? [...attachedImages] : undefined;

      // Append hosted URLs to the text so the agent has a reference even when
      // the image couldn't be embedded as base64 (CORS-blocked CDN). Mirrors
      // what the Marcus managed-chat path does.
      let text = textValue;
      if (images && images.length > 0) {
        const urls = images.map((img) => img.url).join("\n");
        const allImages = images.every((img) => img.type === "image");
        text += `\n\n[Attached ${allImages ? "image" : "media"}${images.length > 1 ? "s" : ""}]\n${urls}`;
      }
      // useManagedAgentChat's sendMessage needs non-empty text.
      if (!text.trim()) text = "(see attached image)";
      sendMessage(text, images);
      setInputValue("");
      clearAttachments();
      clearAttachmentError();
    },
    [attachedImages, clearAttachments, clearAttachmentError, sendMessage],
  );

  const speech = useSpeechRecognition({
    onTranscriptReady: (text: string) => {
      if (!voiceInputEnabled) return;
      sendInputMessage(text);
    },
    interruptAssistantPlayback: () => {},
  });

  useEffect(() => {
    if (!voiceInputEnabled || !speech.isListening) return;
    const poll = setInterval(() => {
      const live = speech.liveDisplayRef.current;
      if (live) setInputValue(live);
    }, 100);
    return () => clearInterval(poll);
  }, [voiceInputEnabled, speech.isListening, speech.liveDisplayRef]);

  // Drain queued input-text pushed from quick-task pickers (AdsCliQuickTasks
  // for meta_ads). Fills the input so the user can edit placeholders before
  // sending, rather than auto-firing a half-templated prompt at the agent.
  // The campaign wizard hand-off sets `autoSubmit: true` so the launch brief
  // goes straight to the agent instead of waiting for an extra Send press.
  const pendingSubAgentInput = useAdsWorkspaceStore((s) => s.pendingSubAgentInput);
  useEffect(() => {
    if (!pendingSubAgentInput) return;
    if (pendingSubAgentInput.agentId !== agentId) return;
    const { text, autoSubmit } = pendingSubAgentInput;
    useAdsWorkspaceStore.getState().clearPendingSubAgentInput();
    if (autoSubmit) {
      sendMessage(text);
      setInputValue("");
      return;
    }
    setInputValue(text);
    // Defer focus so the textarea is in the DOM and ready to accept caret.
    window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }, [pendingSubAgentInput, agentId, sendMessage]);

  const handleSend = useCallback(() => {
    if (speech.isListening) speech.stopListening();
    sendInputMessage(inputValue);
  }, [inputValue, sendInputMessage, speech]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleBack = useCallback(() => {
    setActiveSubAgent(null);
  }, [setActiveSubAgent]);

  // Sanitize the first dispatch-brief user message (drops tool-use framing,
  // reference URLs, and system-prompt pointers; keeps product/aspect/angle).
  // For Banan static-ads dispatches we also rewrite the agent's chatty final
  // turn — the `[Attached media]` strip already renders the variants, so the
  // verbose URL-listing narrative is duplicate noise. Pure reference-
  // preserving transforms, so this memo stays cheap for token deltas.
  const sanitizedMessages = useMemo(() => {
    const next = sanitizeDispatchBriefMessages(messages);
    if (agentId === "banan") {
      return sanitizeImageAdAssistantResults(next);
    }
    if (agentId === "agent_v") {
      return sanitizeAgentVDisplayMessages(next);
    }
    return next;
  }, [messages, agentId]);

  const displaySessionPills = useMemo(() => {
    if (agentId !== "agent_v") return sessionPills;
    return sessionPills.map((session) => ({
      ...session,
      label: sanitizeAgentVProviderBranding(session.label),
    }));
  }, [agentId, sessionPills]);

  const displayMessages: UIMessage[] = useMemo(() => {
    return sanitizedMessages.length === 0
      ? [
          {
            id: `greeting-${agentId}`,
            role: "assistant" as const,
            parts: [{ type: "text" as const, text: meta.greeting }],
          },
        ]
      : sanitizedMessages;
  }, [sanitizedMessages, agentId, meta.greeting]);

  return (
    <div className="flex flex-col h-full bg-background/30">
      {/* ── Header (hidden when parent provides its own via ChatPanelHeader) ── */}
      {!hideHeader && (
        <div
          className="shrink-0 px-4 py-3 flex items-center gap-3 border-b border-border/20"
          style={{ backgroundColor: `${meta.chatBgHex}08` }}
        >
          <button
            onClick={handleBack}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted/50 transition-colors"
            title="Back to Marcus"
          >
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>

          <div
            className={cn("flex items-center justify-center w-8 h-8 rounded-xl")}
            style={{ backgroundColor: `${meta.chatBgHex}20` }}
          >
            <Icon className="w-4 h-4" style={{ color: meta.chatBgHex }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground leading-tight truncate">
              {meta.chatName}
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
              {meta.chatRole}
            </div>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-1.5">
            <div
              className={cn("w-2 h-2 rounded-full", isLoading ? "animate-pulse" : "")}
              style={{ backgroundColor: isLoading ? meta.chatBgHex : "#22c55e" }}
            />
            <span className="text-[9px] text-muted-foreground font-medium">
              {isLoading ? "Thinking..." : "Online"}
            </span>
          </div>
        </div>
      )}

      {/* ── Session navigation pills ── */}
      <SessionPillBar
        sessions={displaySessionPills}
        activeSessionId={activePillSessionId}
        onSelect={handleSelectSession}
        accentColor={meta.chatBgHex}
        runningSessionIds={isLoading && liveSessionId ? [liveSessionId] : undefined}
      />

      {/* ── Messages (compact mode — matches Marcus side-panel chat) ── */}
      {isRestoringHistory ? (
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          <div className="flex flex-col gap-4 pt-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-2 animate-pulse">
                <div className="w-6 h-6 rounded-full bg-muted/40 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-muted/40 rounded w-3/4" />
                  <div className="h-3 bg-muted/40 rounded w-1/2" />
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground text-center">Loading session history...</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-2 ds-scrollbar min-h-0">
          <div className="flex flex-col gap-2 pt-2">
            <ChatMessageList
              messages={displayMessages}
              persona={{ humanName: meta.chatName }}
              compact
              renderToolPart={renderToolPart}
              mediaAutoPreview={meta.mediaPreviewBehavior === "auto-generated-media"}
            />
            {showThinkingBubble ? (
              <ThinkingBubble name={meta.chatName} elapsedMs={turnElapsedMs} />
            ) : lastTurnDurationMs != null && messages.length > 0 ? (
              <TurnCompletedPill ms={lastTurnDurationMs} />
            ) : null}
            <div ref={chatEndRef} />
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="shrink-0 px-4 py-2 bg-red-500/10 text-red-400 text-xs">{error.message}</div>
      )}

      {/* ── Input ── */}
      <div className="shrink-0 bg-card/30 border-t border-border/10">
        <CompanionInput
          inputRef={inputRef}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={handleSend}
          onStop={stopMessage}
          onKeyDown={handleKeyDown}
          isLoading={isLoading}
          compact
          placeholder={`Message ${meta.chatName}...`}
          attachedImages={attachedImages}
          onImageAttach={handleImageAttach}
          onRemoveImage={handleRemoveImage}
          isUploadingImage={isUploadingImage}
          attachmentError={attachmentError}
          showContextPicker={false}
          showMicButton={voiceInputEnabled}
          onToggleListen={voiceInputEnabled ? speech.toggleListen : undefined}
          onCancelListen={voiceInputEnabled ? speech.stopListening : undefined}
          isListening={voiceInputEnabled ? speech.isListening : false}
          isVoiceProcessing={voiceInputEnabled ? speech.isProcessingVoice : false}
          isSecureMicContext={voiceInputEnabled ? speech.isSecureMicContext : true}
          micHint={voiceInputEnabled ? speech.micHint : null}
          queuedMessageCount={queuedMessageCount}
        />
      </div>
    </div>
  );
}
